import React, { useState, useEffect } from 'react';
import { NavItem, NavLink } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import authService from '../../services/authService';

const LoginMenu = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Helper to check if current page is a public page (no auth required)
    const isPublicPage = () => {
      const path = window.location.pathname || '';
      const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/confirm-email', '/app-info', '/docs', '/pricing', '/'];
      return publicPaths.some(p => path === p || path.startsWith(p + '/'));
    };

    const checkAuth = () => {
      // Always check actual auth status, even on public pages
      // Public pages don't require auth, but we should still show if user is logged in
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      if (authenticated) {
        const currentUser = authService.getCurrentUser();
        setUser(currentUser);
      } else {
        setUser(null);
      }
    };

    checkAuth();
    
    // Always poll to keep auth state up to date, regardless of page type
    // This ensures header shows correct state even when navigating between public/private pages
    const interval = setInterval(checkAuth, 1000); // Check every second
    
    // Listen for authentication state changes (e.g., after login)
    const handleAuthChange = () => {
      // Small delay to ensure localStorage is updated
      setTimeout(() => checkAuth(), 100);
    };
    window.addEventListener('authStateChanged', handleAuthChange);
    
    // Also listen for storage changes (localStorage updates)
    const handleStorageChange = (e) => {
      // Check if user was added to localStorage (login) or removed (logout)
      if (e.key === 'user' || e.key === null) {
        setTimeout(() => checkAuth(), 100);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Check auth status when window regains focus (user returns to tab/window)
    const handleFocus = () => {
      checkAuth();
    };
    window.addEventListener('focus', handleFocus);
    
    // Force a refresh after page loads to ensure header is correct
    // This handles cases where auth state is set but component hasn't updated yet
    const refreshTimeout = setTimeout(() => {
      checkAuth();
    }, 500);
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      clearTimeout(refreshTimeout);
      window.removeEventListener('authStateChanged', handleAuthChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [location.pathname]); // Re-run when route changes

  const handleLogout = async () => {
    try {
      // Defer to custom modal instead of native prompt
      try {
        const path = String(window.location.pathname || '');
        const hasUnsaved = sessionStorage.getItem('ai_unsaved') === '1';
        if (path.startsWith('/ai') && hasUnsaved) {
          sessionStorage.setItem('ai_nav_confirm_to', '/');
          const ev = new CustomEvent('ai:confirm-leave');
          window.dispatchEvent(ev);
          return;
        }
      } catch {}
      
      // Clear state immediately to prevent UI flash
      setIsAuthenticated(false);
      setUser(null);
      
      // Then call logout API
      await authService.logout();
      
      // Force page reload to clear any remaining state
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if API call fails, clear local state and redirect
      setIsAuthenticated(false);
      setUser(null);
      window.location.href = '/';
    }
  };

  if (isAuthenticated) {
    return (
      <>
        <NavItem>
          <NavLink
            tag={Link}
            className="text-dark user-name-link"
            to="/dashboard"
            onClick={(e) => {
              try {
                const path = String(window.location.pathname || '');
                const leavingAi = path.startsWith('/ai') && '/dashboard' !== '/ai';
                const hasUnsaved = sessionStorage.getItem('ai_unsaved') === '1';
                if (leavingAi && hasUnsaved) {
                  e.preventDefault();
                  sessionStorage.setItem('ai_nav_confirm_to', '/dashboard');
                  const ev = new CustomEvent('ai:confirm-leave');
                  window.dispatchEvent(ev);
                }
              } catch {}
            }}
          >
            Hello, {user?.firstName || 'User'}!
          </NavLink>
        </NavItem>
        {/* Feedback removed per requirements */}
        <NavItem>
          <button 
            onClick={handleLogout}
            className="btn btn-link navbar-btn text-dark"
            style={{ border: 'none', background: 'none', cursor: 'pointer' }}
          >
            Logout
          </button>
        </NavItem>
      </>
    );
  }

  return null; // Don't show register/login here since they're handled in NavMenu now
};

export default LoginMenu; 